import type { QueryClient } from 'react-query';
import type { ILead } from 'src/services/api';
import type { IUser } from 'src/api/users-api';
import { queryKeys } from 'src/services/api/constants';

export interface UserNotificationSettings {
    notifications: {
        new_message: 'all' | 'owner_only' | 'off';
    };
}

/**
 * Determines if a push notification should be shown based on user settings and lead assignment.
 *
 * Rules:
 * - 'off': never show
 * - 'all': always show
 * - 'owner_only': show only if lead is assigned to current user
 */
export const shouldShowNotification = (
    userSettings: UserNotificationSettings | undefined,
    leadUuid: string | undefined,
    queryClient: QueryClient
): boolean => {
    // If notifications are disabled, don't show
    if (userSettings?.notifications.new_message === 'off') {
        return false;
    }

    // If setting is 'all', show notification
    if (userSettings?.notifications.new_message === 'all') {
        return true;
    }

    // If setting is 'owner_only', check if lead is assigned to current user
    if (userSettings?.notifications.new_message === 'owner_only') {
        if (!leadUuid) {
            return false;
        }

        // Get the specific lead from query cache using per-lead key
        const lead = queryClient.getQueryData<ILead>(
            [queryKeys.leads, leadUuid]
        );

        // Get current user from query cache
        const currentUser = queryClient.getQueryData<IUser>(
            queryKeys.loggedInUser
        );

        // Only show if lead exists, current user exists, and lead is assigned to current user
        return !!(lead && currentUser && lead.owner === currentUser.id);
    }

    return false;
};
